/**
 * Pill-style chip rendering an agent's availability status.
 *
 * Status ids match `dbo.Status` (1 Unavailable, 2 Available, 3 Paused,
 * 4 DnD). Defining the palette in one place lets every page that lists
 * agents/destinations show the same colours without copy-pasting CSS.
 */

export const STATUS_COLORS: Record<number, { bg: string; fg: string; bd: string }> = {
  1: { bg: "#fdecea", fg: "#c0392b", bd: "#e74c3c" },
  2: {
    bg: "var(--portal-status-success-soft)",
    fg: "var(--portal-status-success-darker)",
    bd: "var(--portal-status-success)",
  },
  3: { bg: "#fff3cd", fg: "#856404", bd: "#ffeeba" },
  4: {
    bg: "var(--portal-surface)",
    fg: "var(--portal-muted)",
    bd: "var(--portal-border)",
  },
};

export function AgentStatusChip({
  statusId,
  statusName,
  size = "md",
}: {
  statusId: number;
  statusName: string;
  size?: "sm" | "md";
}) {
  const palette = STATUS_COLORS[statusId] ?? STATUS_COLORS[1];
  const padding = size === "sm" ? "1px 8px" : "3px 10px";
  const fontSize = size === "sm" ? "0.72rem" : "0.8rem";
  return (
    <span
      style={{
        display: "inline-block",
        padding,
        borderRadius: 999,
        border: "1px solid",
        fontSize,
        fontWeight: 600,
        background: palette.bg,
        color: palette.fg,
        borderColor: palette.bd,
        whiteSpace: "nowrap",
      }}
    >
      {statusName || "—"}
    </span>
  );
}

/** Compact variant: a coloured dot with the status name as tooltip (dense lists). */
export function AgentStatusDot({ statusId, statusName }: { statusId: number; statusName: string }) {
  const palette = STATUS_COLORS[statusId] ?? STATUS_COLORS[1];
  return (
    <span
      title={statusName}
      aria-label={statusName}
      role="img"
      style={{ display: "inline-block", width: 12, height: 12, borderRadius: "50%", background: palette.bd, border: `2px solid ${palette.bg}`, boxShadow: `0 0 0 1px ${palette.bd}` }}
    />
  );
}
